import { Endpoint } from '@/constants';
import { getAntiForgeryToken } from '@/utils/getAntiForgeryToken';

interface QuickPayShippingMethod {
  identifier: string;
  label: string;
  detail: string;
  amount: string;
}

interface CouponUpdateResponse {
  success: boolean;
  cartData?: any;
  quickPayShippingMethods?: QuickPayShippingMethod[];
  errors?: Array<{
    code: 'couponCodeInvalid' | 'connectionError';
    message: string;
  }>;
}

/**
 * Handles coupon code changes by calling backend endpoints
 * and returning updated cart and shipping data.
 * Used by both Apple Pay and Google Pay.
 */
export async function handleCouponCode(
  couponCode: string,
  cartName?: string,
  shippingCountryCode?: string
): Promise<CouponUpdateResponse> {
  try {
    let cartResponse;

    // Prepare base request body
    const baseBody = cartName ? { cartName } : {};

    // Log the exact value received for debugging
    console.log('QuickPay: Coupon code changed, received value:', JSON.stringify(couponCode), 'type:', typeof couponCode);

    // More explicit handling: check for all falsy values or empty string after trim
    if (!couponCode || couponCode.trim() === '') {
      // Clear discount code if empty, null, undefined, or whitespace-only
      console.log('QuickPay: Clearing discount code (empty/null/undefined)');

      const clearResponse = await fetch(Endpoint.ClearDiscountCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        },
        body: JSON.stringify(baseBody)
      });

      if (!clearResponse.ok) {
        console.error('QuickPay: Failed to clear discount code - HTTP error', clearResponse.status);
        return {
          success: false,
          errors: [{
            code: 'connectionError',
            message: 'Unable to clear discount code. Please try again.'
          }]
        };
      }

      cartResponse = await clearResponse.json();

      // For clearing operations, always return success even if backend says there's nothing to clear
      // This ensures the payment sheet clears any error state when user clears the field
      if (!cartResponse.isSuccess) {
        console.log('QuickPay: Clear discount returned false (likely no discount to clear), treating as success');
        // Still use the cart data from response, just ensure we return success
        cartResponse.isSuccess = true;
      }
    } else {
      // Add discount code (non-empty value)
      console.log('QuickPay: Adding discount code:', couponCode);

      const addResponse = await fetch(Endpoint.AddDiscountCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        },
        body: JSON.stringify({
          discountCode: couponCode.trim(),
          ...baseBody
        })
      });

      if (!addResponse.ok) {
        console.error('QuickPay: Failed to apply discount code - HTTP error', addResponse.status);
        return {
          success: false,
          errors: [{
            code: 'connectionError',
            message: 'Unable to apply discount code. Please try again.'
          }]
        };
      }

      cartResponse = await addResponse.json();
    }

    // Check if the operation was successful
    if (!cartResponse.isSuccess) {
      // If coupon was invalid, return error with message from backend
      // Note: This will only trigger for AddDiscountCode failures now
      const errorMessage = cartResponse.messages?.[0] || 'Invalid coupon code';
      console.warn('QuickPay: Coupon operation failed:', errorMessage);
      return {
        success: false,
        errors: [{
          code: 'couponCodeInvalid',
          message: errorMessage
        }]
      };
    }

    // Extract cart data from response
    const cartData = cartResponse.data;

    // Check if we need to update shipping options
    // (free shipping threshold may have changed with discount)
    let quickPayShippingMethods: QuickPayShippingMethod[] = [];

    try {
      const countryCode = shippingCountryCode || 'IE';  // Default to IE if not provided
      const shippingUrl = cartName
        ? `${Endpoint.GetShippingOptions}?countryCode=${countryCode}&cartName=${encodeURIComponent(cartName)}`
        : `${Endpoint.GetShippingOptions}?countryCode=${countryCode}`;

      const shippingResponse = await fetch(shippingUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        }
      });

      if (shippingResponse.ok) {
        const shippingData = await shippingResponse.json();

        if (shippingData.isSuccess && shippingData.data) {
          // Convert to QuickPay format
          quickPayShippingMethods = shippingData.data.map((option: any) => ({
            identifier: option.type || option.id,
            label: option.title,
            detail: option.description,
            amount: option.priceMoney?.amount?.toString() || '0.00'
          }));
        }
      }
    } catch (shippingError) {
      // Log but don't fail if shipping options update fails
      console.warn('QuickPay: Could not update shipping options after coupon change:', shippingError);
    }

    return {
      success: true,
      cartData,
      quickPayShippingMethods
    };

  } catch (error) {
    console.error('QuickPay: Unexpected error in coupon code handler:', error);
    return {
      success: false,
      errors: [{
        code: 'connectionError',
        message: 'An unexpected error occurred. Please try again.'
      }]
    };
  }
}